/**
 * Record detail views for the health hub (#847, D-F).
 *
 * Every record's name clicks through to one of these dialogs — the "source"
 * view where the full fields live alongside edit, delete, archive and status
 * changes. Cards stay clean (the epic's cognitive-load guard); the detail
 * view carries the affordances. Deep-linkable via `?tab=X&id=Y` — HealthPage
 * owns that wiring and simply mounts these with `open`.
 *
 * Destructive actions confirm in-app through the shared <ConfirmActionDialog>
 * (shadcn AlertDialog — no browser confirm() anywhere, D-C). Whether a delete
 * offers undo is the CALLER's decision, because honesty about undo depends on
 * what re-creation can actually restore — see HealthPage's delete handlers.
 *
 * The medication detail additionally renders the dose timeline from the #846
 * dose-log API: stored taken/skipped assertions with timestamps and notes,
 * plus derived pending/missed rows (absence of a log, never presence — D-A).
 * Missed is amber `warn`, never red (D-I).
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  AlertTriangle,
  CalendarDays,
  HelpCircle,
  Pencil,
  Pill,
  Trash2,
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription } from '@/components/ui/dialog';
import { ConfirmActionDialog } from '@/components/ui/ConfirmActionDialog';
import { ModalHeader } from '@/components/admin/editorial-modal';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MonoLabel, StatusChip, type ChipTone } from '@/components/editorial';
import { cn } from '@/lib/utils';
import { safeFormatIntl, dateOnlyIntl } from '@/lib/safe-date';
import { api } from '@/services/api';
import { labelFor } from '@/i18n/labels';
import { formatList } from '@/i18n/intl';
import { useWeekdayDate } from './useWeekdayDate';
import {
  DOSE_STATUS_LABEL_KEY,
  MED_FORM_LABEL_KEY,
  MED_FREQUENCY_LABEL_KEY,
  QUESTION_CATEGORY_LABEL_KEY,
  QUESTION_PRIORITY_LABEL_KEY,
  SLOT_LABEL_KEY,
  SYMPTOM_SEVERITY_LABEL_KEY,
  SYMPTOM_SEVERITY_TOKENS,
  SYMPTOM_TYPE_LABEL_KEY,
  buildDoseTimeline,
  reactionsForMedication,
  type DoseTimelineStatus,
} from '@/lib/medical-display';
import type {
  DoctorQuestion,
  MedicalAppointment,
  Medication,
  SymptomEntry,
} from '@/types/api';

// ── Shared shell ──────────────────────────────────────────────────────────

export interface DetailShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kicker: string;
  title: string;
  icon: LucideIcon;
  children: React.ReactNode;
  footer: React.ReactNode;
}

/** Exported for `components/calendar/AppointmentDetailDialog.tsx` (moved out in #2109 R3 L0); keep the props stable. */
export function DetailShell({ open, onOpenChange, kicker, title, icon, children, footer }: DetailShellProps) {
  const { t } = useTranslation(['health', 'common']);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-xl flex flex-col p-0 gap-0 max-h-[90vh] focus:outline-none"
        // #2418: Radix focuses the first tabbable element on open, which here
        // was the red Delete button leading the footer. Focus the dialog
        // itself instead; Tab then walks the fields and the footer in order.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement | null)?.focus();
        }}
      >
        <ModalHeader icon={icon} eyebrow={kicker} title={title} />
        {/* Radix warns without a description; the field list is the description. */}
        <DialogDescription className="sr-only">{t('health:detail.recordDetails')}</DialogDescription>
        <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-4 min-h-0">{children}</div>
        <footer className="flex flex-wrap items-center justify-end gap-2 border-t px-6 py-3.5 flex-shrink-0">
          {footer}
        </footer>
      </DialogContent>
    </Dialog>
  );
}

/** Exported with `DetailShell` for the Calendar appointment dialog. */
export function DetailField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[130px_1fr] gap-3 items-baseline text-body">
      <MonoLabel>{label}</MonoLabel>
      <div className="text-foreground/90 min-w-0">{children}</div>
    </div>
  );
}

// ── In-app confirm ────────────────────────────────────────────────────────
//
// Shared under components/ui since 2026-09-10; re-exported for this file's importers.
export { ConfirmActionDialog };
export type { ConfirmActionDialogProps } from '@/components/ui/ConfirmActionDialog';

// ── Dose timeline (medication detail) ─────────────────────────────────────

const DOSE_STATUS_TONE: Record<DoseTimelineStatus, ChipTone> = {
  taken:   'ok',
  skipped: 'neutral',
  // D-I: a missed home dose is routine treatment reality — amber, never red.
  missed:  'warn',
  pending: 'neutral',
};

const DOSE_WINDOW_DAYS = 14;

function localDateKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function DoseTimeline({ med, open }: { med: Medication; open: boolean }) {
  const { t } = useTranslation(['health', 'common']);
  const weekdayDate = useWeekdayDate();
  // Stable per-day range keys so the query doesn't churn while the dialog is
  // open. Family-wide fetch (the API has no per-med filter); buildDoseTimeline
  // filters to this medication client-side.
  const { startKey, endKey } = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - (DOSE_WINDOW_DAYS - 1));
    return { startKey: localDateKey(start), endKey: localDateKey(now) };
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['medical', 'doses', startKey, endKey],
    queryFn: () => api.medical.doses.getRange(startKey, endKey),
    enabled: open,
  });

  const entries = useMemo(
    () => (data?.data ? buildDoseTimeline(med, data.data, { days: DOSE_WINDOW_DAYS }) : []),
    [data, med],
  );

  return (
    <section className="space-y-2">
      <div className="flex items-baseline justify-between">
        <MonoLabel>{t('health:detail.doseLog.title', { count: DOSE_WINDOW_DAYS })}</MonoLabel>
        <span className="text-stevens-ink-3">
          {t('health:detail.doseLog.note')}
        </span>
      </div>
      {isLoading ? (
        <p className="text-caption text-stevens-ink-3">{t('health:detail.doseLog.loading')}</p>
      ) : error ? (
        <p className="text-caption text-stevens-ink-3">{t('health:detail.doseLog.loadFailed')}</p>
      ) : entries.length === 0 ? (
        <p className="text-caption text-stevens-ink-3">
          {med.frequency === 'as_needed' || med.frequency === 'weekly' || (med.time_of_day?.length ?? 0) === 0
            ? t('health:detail.doseLog.noneLogged')
            : t('health:detail.doseLog.noneScheduled')}
        </p>
      ) : (
        <ul className="divide-y divide-border/60 rounded-lg border border-border/60">
          {entries.map((e) => (
            <li key={`${e.date}-${e.slot}`} className="flex items-start gap-3 px-3 py-2 text-caption">
              <span className="w-[72px] flex-shrink-0 font-mono text-stevens-ink-3">
                {weekdayDate(e.date)}
              </span>
              <span className="w-[64px] flex-shrink-0 text-stevens-ink-3">{labelFor(SLOT_LABEL_KEY, e.slot, t)}</span>
              <StatusChip tone={DOSE_STATUS_TONE[e.status]}>{labelFor(DOSE_STATUS_LABEL_KEY, e.status, t)}</StatusChip>
              <span className="flex-1 min-w-0 text-stevens-ink-3">
                {e.logged_at && (
                  <span className="font-mono">{safeFormatIntl(e.logged_at, 'monthDayTime')}</span>
                )}
                {e.note && <span className="ml-2 italic font-serif">{t('health:quoted', { text: e.note })}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── Medication detail ─────────────────────────────────────────────────────

interface MedicationDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  med: Medication;
  symptoms: SymptomEntry[];
  onEdit: (med: Medication) => void;
  /** Archive/reactivate: `active:false` + end_date, or `active:true` (D-C). */
  onToggleActive: (med: Medication) => void;
  onDelete: (med: Medication) => void;
  isMutating?: boolean;
}

export function MedicationDetailDialog({
  open,
  onOpenChange,
  med,
  symptoms,
  onEdit,
  onToggleActive,
  onDelete,
  isMutating,
}: MedicationDetailDialogProps) {
  const { t } = useTranslation(['health', 'common']);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const reactionCount = reactionsForMedication(symptoms, med._id);
  const frequency = labelFor(MED_FREQUENCY_LABEL_KEY, med.frequency, t);

  return (
    <>
      <DetailShell
        open={open}
        onOpenChange={onOpenChange}
        kicker={med.active ? t('health:detail.medication.kicker') : t('health:detail.medication.kickerInactive')}
        title={med.medication_name}
        icon={Pill}
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              className="mr-auto text-destructive hover:text-destructive"
              disabled={isMutating}
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {t('common:actions.delete')}
            </Button>
            <Button variant="outline" size="sm" disabled={isMutating} onClick={() => onToggleActive(med)}>
              {med.active ? t('health:detail.medication.markInactive') : t('health:detail.medication.reactivate')}
            </Button>
            <Button variant="brand" size="sm" disabled={isMutating} onClick={() => onEdit(med)}>
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> {t('common:actions.edit')}
            </Button>
          </>
        }
      >
        {med.generic_name && med.generic_name !== med.medication_name && (
          <DetailField label={t('health:detail.medication.genericName')}>{med.generic_name}</DetailField>
        )}
        <DetailField label={t('health:detail.medication.dose')}>
          <span className="font-mono">{med.dose}</span>
          {/* The form's label, not the raw enum (R3-B22). */}
          {med.form ? <>{' · '}{labelFor(MED_FORM_LABEL_KEY, med.form, t)}</> : null}
        </DetailField>
        <DetailField label={t('health:detail.medication.frequency')}>
          {med.with_food ? t('health:detail.medication.frequencyWithFood', { frequency }) : frequency}
        </DetailField>
        {(med.time_of_day?.length ?? 0) > 0 && (
          <DetailField label={t('health:detail.medication.timeOfDay')}>
            {/* A plain list ("Morning, Evening"), not a sentence: the unit list shape. */}
            {formatList(med.time_of_day.map((s) => labelFor(SLOT_LABEL_KEY, s, t)), { type: 'unit', style: 'short' })}
          </DetailField>
        )}
        {med.purpose && <DetailField label={t('health:detail.medication.purpose')}>{med.purpose}</DetailField>}
        {med.prescribing_doctor && (
          <DetailField label={t('health:detail.medication.prescribedBy')}>{med.prescribing_doctor}</DetailField>
        )}
        {/* start/end/refill are date-only fields (#1308) — render the stored
            calendar day, not that day's UTC midnight in the reader's zone. */}
        {med.start_date && (
          <DetailField label={t('health:detail.medication.started')}>{dateOnlyIntl(med.start_date, 'monthDayYear')}</DetailField>
        )}
        {med.end_date && (
          <DetailField label={t('health:detail.medication.ended')}>{dateOnlyIntl(med.end_date, 'monthDayYear')}</DetailField>
        )}
        {med.refill_reminder && med.refill_date && (
          <DetailField label={t('health:detail.medication.refill')}>{dateOnlyIntl(med.refill_date, 'monthDayYear')}</DetailField>
        )}
        {reactionCount > 0 && (
          <DetailField label={t('health:detail.medication.reactions')}>
            <span className="inline-flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-status-warn-fg" />
              {t('health:detail.medication.reactionsValue', { count: reactionCount })}
            </span>
          </DetailField>
        )}
        <DoseTimeline med={med} open={open} />
      </DetailShell>

      <ConfirmActionDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('health:detail.medication.confirmTitle', { name: med.medication_name })}
        description={t('health:detail.medication.confirmBody')}
        confirmLabel={t('health:detail.medication.confirmLabel')}
        onConfirm={() => {
          setConfirmDelete(false);
          onDelete(med);
        }}
      />
    </>
  );
}

// ── Question detail ───────────────────────────────────────────────────────

interface QuestionDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  q: DoctorQuestion;
  linkedAppt?: MedicalAppointment;
  onEdit: (q: DoctorQuestion) => void;
  onDelete: (q: DoctorQuestion) => void;
  onAddFollowUp?: (q: DoctorQuestion) => void;
  isMutating?: boolean;
}

export function QuestionDetailDialog({
  open,
  onOpenChange,
  q,
  linkedAppt,
  onEdit,
  onDelete,
  onAddFollowUp,
  isMutating,
}: QuestionDetailDialogProps) {
  const { t } = useTranslation(['health', 'common']);
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <>
      <DetailShell
        open={open}
        onOpenChange={onOpenChange}
        kicker={q.answered ? t('health:detail.question.kickerAnswered') : t('health:detail.question.kicker')}
        title={q.question}
        icon={HelpCircle}
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              className="mr-auto text-destructive hover:text-destructive"
              disabled={isMutating}
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {t('common:actions.delete')}
            </Button>
            {q.answered && onAddFollowUp && (
              <Button variant="outline" size="sm" disabled={isMutating} onClick={() => onAddFollowUp(q)}>
                {t('health:card.addFollowUp')}
              </Button>
            )}
            <Button variant="brand" size="sm" disabled={isMutating} onClick={() => onEdit(q)}>
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> {t('common:actions.edit')}
            </Button>
          </>
        }
      >
        <DetailField label={t('health:detail.question.priority')}>{labelFor(QUESTION_PRIORITY_LABEL_KEY, q.priority, t)}</DetailField>
        <DetailField label={t('health:detail.question.category')}>{labelFor(QUESTION_CATEGORY_LABEL_KEY, q.category, t)}</DetailField>
        {linkedAppt && (
          <DetailField label={t('health:detail.question.forVisit')}>
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays className="h-3.5 w-3.5 text-stevens-ink-3" />
              {t('health:detail.question.forVisitValue', { title: linkedAppt.title, date: safeFormatIntl(linkedAppt.date_time, 'monthDayYear') })}
            </span>
          </DetailField>
        )}
        <DetailField label={t('health:detail.question.added')}>{safeFormatIntl(q.created_at, 'monthDayYear')}</DetailField>
        {q.answered && (
          <DetailField label={t('health:detail.question.answer')}>
            {q.answer_notes ? (
              <span className="font-serif italic">{t('health:quoted', { text: q.answer_notes })}</span>
            ) : (
              t('health:detail.question.markedAnswered')
            )}
          </DetailField>
        )}
      </DetailShell>

      <ConfirmActionDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('health:detail.question.confirmTitle')}
        description={
          q.answered
            ? t('health:detail.question.confirmBodyAnswered')
            : t('health:detail.question.confirmBody')
        }
        confirmLabel={t('health:detail.question.confirmLabel')}
        onConfirm={() => {
          setConfirmDelete(false);
          onDelete(q);
        }}
      />
    </>
  );
}

// ── Symptom detail ────────────────────────────────────────────────────────

interface SymptomDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  s: SymptomEntry;
  linkedMed?: Medication;
  onEdit: (s: SymptomEntry) => void;
  onDelete: (s: SymptomEntry) => void;
  isMutating?: boolean;
}

export function SymptomDetailDialog({
  open,
  onOpenChange,
  s,
  linkedMed,
  onEdit,
  onDelete,
  isMutating,
}: SymptomDetailDialogProps) {
  const { t } = useTranslation(['health', 'common']);
  const weekdayDate = useWeekdayDate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const severity = SYMPTOM_SEVERITY_TOKENS[s.severity];

  return (
    <>
      <DetailShell
        open={open}
        onOpenChange={onOpenChange}
        kicker={t('health:detail.symptom.kicker')}
        title={s.description}
        icon={Activity}
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              className="mr-auto text-destructive hover:text-destructive"
              disabled={isMutating}
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {t('common:actions.delete')}
            </Button>
            <Button variant="brand" size="sm" disabled={isMutating} onClick={() => onEdit(s)}>
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> {t('common:actions.edit')}
            </Button>
          </>
        }
      >
        {/* Date-only (#1308). The `h:mm a` half printed UTC midnight in the
            reader's zone — a time nobody entered, on the wrong day. */}
        <DetailField label={t('health:detail.symptom.when')}>{weekdayDate(s.date, { dateOnly: true, year: true })}</DetailField>
        <DetailField label={t('health:detail.symptom.type')}>{labelFor(SYMPTOM_TYPE_LABEL_KEY, s.type, t)}</DetailField>
        <DetailField label={t('health:detail.symptom.severity')}>
          <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-label font-medium uppercase', severity.pill)}>
            {labelFor(SYMPTOM_SEVERITY_LABEL_KEY, s.severity, t)}
          </span>
        </DetailField>
        {linkedMed && (
          <DetailField label={t('health:detail.symptom.medication')}>
            <StatusChip tone="violet">
              <Pill className="h-3 w-3" />
              {linkedMed.medication_name}
            </StatusChip>
          </DetailField>
        )}
        {s.what_helped && <DetailField label={t('health:detail.symptom.whatHelped')}>{s.what_helped}</DetailField>}
        <DetailField label={t('health:detail.symptom.careTeam')}>
          {s.reported_to_doctor ? (
            <StatusChip tone="ok">{t('health:card.reported')}</StatusChip>
          ) : (
            // Not-yet-reported is a to-do, not a crisis — neutral here; the
            // page-level banner carries the severe-and-recent urgency.
            <span className="text-stevens-ink-3">{t('health:detail.symptom.notReported')}</span>
          )}
        </DetailField>
        {s.doctor_response && (
          <DetailField label={t('health:detail.symptom.doctorSaid')}>
            <span className="font-serif italic">{t('health:quoted', { text: s.doctor_response })}</span>
          </DetailField>
        )}
      </DetailShell>

      <ConfirmActionDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('health:detail.symptom.confirmTitle')}
        description={t('health:detail.symptom.confirmBody')}
        confirmLabel={t('health:detail.symptom.confirmLabel')}
        onConfirm={() => {
          setConfirmDelete(false);
          onDelete(s);
        }}
      />
    </>
  );
}
